import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { useSelector, useDispatch } from 'react-redux'
import { FormattedMessage, useIntl } from 'react-intl'
import { useForm, useWatch } from 'react-hook-form'
import { Link } from 'react-router-dom'
import { Box, Button, Dialog, DialogContent, DialogTitle, Grid, Typography } from '@mui/material'
import { Cancel } from '@mui/icons-material'

import InputFieldSideBySide from 'components/form/InputFieldSideBySide'
import SubmitButton from 'components/form/SubmitButton'
import CountrySelect from 'components/form/CountrySelect'
import CountryStateSelect from 'components/form/CountryStateSelect'
import TimeZoneSelect from 'components/form/TimeZoneSelect'
import LoadingSpinnerGreen from 'components/ui/LoadingSpinnerGreen'
import SuccessMessage from 'components/ui/SuccessMessage'
import { ClientRoute } from 'routes/client'

import { selectClub } from 'store/club/slice'
import { selectStates, clearStates } from 'store/countryStates/slice'
import { getClub, createClub, editClub, generateInitialMapping, unmapClubProviderMapping } from 'store/club/thunks'
import { getStates } from 'store/countryStates/thunks'
import { generateClubFormData } from 'utils/generateClubFormData'
import { generateClubPayload } from 'utils/generateClubPayload'
import { COURSE_PROVIDER } from 'config'

interface Props {
  open: boolean
  toggle: () => void
  clubId: number | null
  hideCoursesButton?: boolean
}

export default function ClubModalForm({ open, toggle, clubId, hideCoursesButton = false }: Props): JSX.Element {
  const [currentCountry, setCurrentCountry] = useState<Country | null>(null)
  const { register, handleSubmit, control, reset } = useForm<ClubFormData>()
  const watch = useWatch({ control })
  const dispatch = useDispatch()
  const intl = useIntl()
  const { club, status, saveStatus, initialMappingStatus, initialMappingError, unmapClubProviderMappingStatus } =
    useSelector(selectClub)
  const countryStates = useSelector(selectStates)

  useEffect(() => {
    if (clubId && (!club || clubId !== club.id)) {
      dispatch(getClub(clubId))
    } else if (!clubId) {
      reset(generateClubFormData(null))
    }
  }, [clubId, club, dispatch, reset])

  useEffect(() => {
    if (status === 'succeeded' && clubId === club.id) {
      reset(generateClubFormData(club))
      setCurrentCountry(club.country)
    }
  }, [club, clubId, status, reset])

  useEffect(() => {
    if (currentCountry?.hasStates) {
      dispatch(getStates(currentCountry.id))
    }
  }, [currentCountry, dispatch])

  useEffect(() => {
    if (!clubId && saveStatus === 'succeeded' && initialMappingStatus === 'idle') {
      dispatch(generateInitialMapping(club.id))
    }
  }, [clubId, saveStatus, initialMappingStatus, club, dispatch])

  const submitForm = (data: ClubFormData) => {
    const payload = generateClubPayload(data)

    if (clubId) {
      dispatch(editClub({ id: clubId, data: payload }))
    } else {
      dispatch(createClub({ data: payload }))
    }
  }

  const handleCountryChange = (countries: Country[]) => {
    if (countries.length > 0) {
      const country = countries[0]
      setCurrentCountry(country)
      dispatch(clearStates())
    } else {
      dispatch(clearStates())
      setCurrentCountry(null)
    }
  }

  const getSaveSuccessMessage = () => {
    return clubId
      ? intl.formatMessage({ id: 'strings.clubSavedSuccessfully' })
      : intl.formatMessage({ id: 'strings.clubCreatedSuccessfully' })
  }

  const getTitlePrefix = () => {
    return clubId ? intl.formatMessage({ id: 'strings.edit' }) : intl.formatMessage({ id: 'strings.addNew' })
  }

  const submitInitialMapping = () => {
    dispatch(generateInitialMapping(club.id))
  }

  const getSaveStatus = () => {
    return saveStatus === 'succeeded' ? initialMappingStatus : saveStatus
  }

  const saveIsFinished = () => {
    return saveStatus === 'succeeded' && initialMappingStatus === 'succeeded'
  }

  const handleUnmap = () => {
    if (!club.providerClubMap?.id) {
      return
    }
    const payload: UnmapClubProviderMappingPayload = {
      newProvider: COURSE_PROVIDER.GGB,
      clubId: club.id,
      newProviderClubId: club.id.toString(),
      clubProviderMapId: club.providerClubMap.id,
      successMessage: intl.formatMessage({ id: 'strings.clubUnmapedSuccessfullyMessage' }),
    }
    dispatch(unmapClubProviderMapping(payload))
  }

  return (
    <StyledDialog open={open} onClose={toggle}>
      <ClubForm onSubmit={handleSubmit(submitForm)}>
        <StyledDialogTitle id="form-dialog-title">
          {getTitlePrefix()} {intl.formatMessage({ id: 'strings.club' })}
          <CloseModal onClick={toggle} />
        </StyledDialogTitle>
        <DialogContent>
          {status === 'loading' && (
            <LoadingContainer>
              <LoadingSpinnerGreen size={62} />
            </LoadingContainer>
          )}
          <Grid container spacing={5}>
            <FormSection item xs={6}>
              <SectionTitle>{intl.formatMessage({ id: 'strings.general' })}</SectionTitle>
              <InputFieldSideBySide
                name="name"
                type="text"
                register={register}
                label={intl.formatMessage({ id: 'strings.clubName' })}
                outlined
                required
              />
              <InputFieldSideBySide
                name="streetAddress"
                type="text"
                register={register}
                label={intl.formatMessage({ id: 'strings.streetAddress' })}
                outlined
                required
              />
              <InputFieldSideBySide
                name="streetAddress2"
                type="text"
                register={register}
                label={`${intl.formatMessage({ id: 'strings.streetAddress' })} 2`}
                outlined
              />
              <InputFieldSideBySide
                name="postalCode"
                type="text"
                register={register}
                label={intl.formatMessage({ id: 'strings.postalCode' })}
                outlined
                required
              />
              <CountrySelect
                name="country"
                control={control}
                initialValue={watch['country'] as SelectOption}
                label={intl.formatMessage({ id: 'strings.country' })}
                labelPosition="left"
                handleCountryChange={handleCountryChange}
                required
              />
              <CountryStateSelect
                name="state"
                control={control}
                countryStates={countryStates}
                labelPosition="left"
                disabled={!currentCountry || currentCountry?.hasStates === false}
                required={currentCountry?.hasStates === true}
                value={watch['state'] as SelectOption}
              />
              <InputFieldSideBySide
                name="city"
                type="text"
                register={register}
                label={intl.formatMessage({ id: 'strings.city' })}
                outlined
                required
              />
              <TimeZoneSelect
                name="timeZone"
                control={control}
                labelPosition="left"
                value={watch['timeZone'] || ''}
                required
              />
              <InputFieldSideBySide
                name="email"
                type="text"
                register={register}
                label={intl.formatMessage({ id: 'strings.email' })}
                outlined
              />
              <InputFieldSideBySide
                name="phone"
                type="text"
                register={register}
                label={intl.formatMessage({ id: 'strings.phone' })}
                outlined
              />
              <InputFieldSideBySide
                name="url"
                type="text"
                register={register}
                label={intl.formatMessage({ id: 'strings.website' })}
                outlined
              />
            </FormSection>
            <FormSection item xs={6}>
              <SectionTitle>{intl.formatMessage({ id: 'strings.billing' })}</SectionTitle>
              <InputFieldSideBySide
                name="billingContactName"
                type="text"
                register={register}
                label={intl.formatMessage({ id: 'strings.contactName' })}
                outlined
              />
              <InputFieldSideBySide
                name="billingContactPhone"
                type="text"
                register={register}
                label={intl.formatMessage({ id: 'strings.contactPhone' })}
                outlined
              />
              <InputFieldSideBySide
                name="billingContactEmail"
                type="text"
                register={register}
                label={intl.formatMessage({ id: 'strings.contactEmail' })}
                outlined
              />
              <InputFieldSideBySide
                name="billingContactAddress"
                type="text"
                register={register}
                label={intl.formatMessage({ id: 'strings.billingAddress' })}
                outlined
              />
              <InputFieldSideBySide
                name="billingContactAddress2"
                type="text"
                register={register}
                label={`${intl.formatMessage({ id: 'strings.billingAddress' })} 2`}
                outlined
              />
              <InputFieldSideBySide
                name="billingContactPostalCode"
                type="text"
                register={register}
                label={intl.formatMessage({ id: 'strings.postalCode' })}
                outlined
              />
              <InputFieldSideBySide
                name="billingContactCity"
                type="text"
                register={register}
                label={intl.formatMessage({ id: 'strings.city' })}
                outlined
              />
              <CountrySelect
                name="billingContactCountry"
                control={control}
                initialValue={watch['billingContactCountry'] as SelectOption}
                label={intl.formatMessage({ id: 'strings.country' })}
                labelPosition="left"
              />
              <Grid
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'right',
                  gap: 3,
                  width: '100%',
                  mt: 1,
                  padding: '4px',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignSelf: 'flex-end',
                    gap: 3,
                    width: '85%',
                    mt: 1,
                    border: 'solid 2px',
                    borderRadius: '10px',
                    padding: '10px',
                    borderColor: '#1AA85D',
                  }}
                >
                  <Typography>
                    <Typography component="span" fontWeight="bold" sx={{ marginRight: '4px' }}>
                      Club Provider:
                    </Typography>{' '}
                    {club.provider?.abbreviation}
                  </Typography>
                  <Typography>
                    <Typography component="span" fontWeight="bold" sx={{ marginRight: '4px' }}>
                      Club Provider ID:
                    </Typography>{' '}
                    {club.providerClubMap?.providerClubId}
                  </Typography>
                  <Button
                    sx={{ border: 'solid 1px', ml: 'auto' }}
                    onClick={handleUnmap}
                    disabled={
                      club.provider?.abbreviation === COURSE_PROVIDER.GGB || club.provider?.abbreviation === undefined
                    }
                  >
                    <Typography>Unmap</Typography>
                  </Button>
                </Box>
              </Grid>
            </FormSection>
          </Grid>
          {club.id > 0 && !club.timeZone && unmapClubProviderMappingStatus === 'idle' && (
            <ValidTimeZone>{intl.formatMessage({ id: 'strings.validTimeZone' })}</ValidTimeZone>
          )}
          <ActionButtons>
            <SubmitButton status={getSaveStatus()} text={<FormattedMessage id={'forms.save'} />} />
            {clubId && !hideCoursesButton && (
              <ViewCoursesButton to={ClientRoute.ADMIN_CLUB_COURSES(club.id)}>
                {intl.formatMessage({ id: 'strings.viewCourses' })}
              </ViewCoursesButton>
            )}

            {initialMappingError && initialMappingStatus === 'failed' && (
              <>
                <SubmitButton
                  status={initialMappingStatus}
                  text={intl.formatMessage({ id: 'strings.retry' })}
                  handleSubmit={submitInitialMapping}
                />
              </>
            )}

            {saveIsFinished() && <SuccessMessage>{getSaveSuccessMessage()}</SuccessMessage>}
          </ActionButtons>
        </DialogContent>
      </ClubForm>
    </StyledDialog>
  )
}

const StyledDialog = styled(Dialog)`
  & .MuiDialog-paper {
    width: 60%;
    max-width: 60%;

    & .MuiDialogContent-root {
      padding-right: 50px;
    }
  }
`

const StyledDialogTitle = styled(DialogTitle)`
  background-color: #404040;
  color: ${(props) => props.theme.customPalette.light};
`

const ActionButtons = styled.div`
  display: flex;
  justify-content: flex-start;
  width: 100%;
  margin-top: 20px;
  padding-left: 110px;

  & button {
    width: 200px;
    height: 50px;
  }
`

const ValidTimeZone = styled.span`
  margin-left: 105px;
  padding: 5px;
  background-color: red;
  color: white;
`

const CloseModal = styled(Cancel)`
  float: right;
  cursor: pointer;
`

const ClubForm = styled.form`
  & .MuiInputLabel-root {
    color: #404040;
    padding: 0;
  }
  & .MuiGrid-root > .MuiFormControl-root {
    margin: 5px 0 10px;
  }
  & .MuiInputBase-formControl {
    background-color: ${(props) => props.theme.customPalette.lightGray};
    min-height: 40px;
    padding: 5px;
    & .MuiInputBase-input {
      padding: 0 0 0 5px;
    }
  }
`

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
`

const FormSection = styled(Grid)`
  margin: 20px 0;
`

const SectionTitle = styled.h5`
  color: ${(props) => props.theme.palette.primary.main};
  font-style: italic;
  font-weight: 800;
  font-size: 20px;
  line-height: 27px;
  padding-left: 110px;
  margin-bottom: 20px;
`

const ViewCoursesButton = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 200px;
  height: 50px;
  margin-left: 30px !important;
  color: ${(props) => props.theme.palette.primary.main};
  border: 2px solid;
  border-color: ${(props) => props.theme.palette.primary.main};
  border-radius: 5px;
  text-decoration: none;
`
